import {
	alpha,
	Avatar,
	Box,
	Button,
	Checkbox,
	Chip,
	Divider,
	IconButton,
	ListItem,
	Menu,
	MenuItem,
	Tooltip,
	Typography
} from '@mui/material';
import { blue, green, grey, orange, red } from "@mui/material/colors";
import { format, isSameDay } from "date-fns";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { makeStyles } from 'tss-react/mui';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CheckIcon from '@mui/icons-material/Check';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import FacebookIcon from '@mui/icons-material/Facebook';
import ForumIcon from '@mui/icons-material/Forum';
import InstagramIcon from '@mui/icons-material/Instagram';
import MicIcon from '@mui/icons-material/Mic';
import ContactIcon from '@mui/icons-material/Person';
import TelegramIcon from '@mui/icons-material/Telegram';
import VideocamIcon from '@mui/icons-material/Videocam';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupIcon from '@mui/icons-material/Group';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import ReplyIcon from '@mui/icons-material/Reply';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TransferWithinAStationIcon from '@mui/icons-material/TransferWithinAStation';

import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import ToastError from "../../toast/error/toastError";
import isColorDark from '../../utils/isColorDark';
import ConfirmationModal from "../ConfirmationModal";
import MarkdownWrapper from "../MarkdownWrapper";
import RatingStars from "../RatingStars";
import ReasonForContact from "../ReasonForContact";
import RescheduleTicket from "../RescheduleTicket";
import ShareGroupModal from "../ShareGroupModal";
import TransferTicketPendingModalCustom from "../TransferTicketPendingModalCustom";

const useStyles = makeStyles()((theme) => ({
	chatItem: {
		padding: '10px 10px 10px 20px',
		cursor: 'pointer',
		transition: 'background-color 0.15s ease-out',
		borderRadius: 0,
		position: 'relative',
		minHeight: '68px',
		width: '100%',
		boxSizing: 'border-box',

		'&:hover': {
			backgroundColor: theme.palette.mode === 'dark'
				? alpha(theme.palette.common.white, 0.05)
				: alpha(theme.palette.common.black, 0.03),
		},

		'&.selected': {
			backgroundColor: theme.palette.mode === 'dark'
				? alpha(theme.palette.primary.main, 0.15)
				: alpha(theme.palette.primary.main, 0.08),
		},

		'&.unread': {
			backgroundColor: theme.palette.mode === 'dark'
				? alpha(theme.palette.common.white, 0.02)
				: alpha(theme.palette.common.black, 0.015),
		},

		'&.pinned': {
			backgroundColor: theme.palette.mode === 'dark'
				? alpha(blue[500], 0.1)
				: alpha(blue[50], 0.6),
		}
	},

	chatLayout: {
		display: 'grid',
		gridTemplateColumns: 'auto 1fr auto',
		alignItems: 'start',
		width: '100%',
		gap: '10px',
		boxSizing: 'border-box',
	},

	avatarSection: {
		position: 'relative',
	},

	avatar: {
		width: 42,
		height: 42,
		fontSize: '16px',
		fontWeight: 600,
		border: theme.palette.mode === 'dark'
			? `2px solid ${alpha(theme.palette.common.white, 0.12)}`
			: `2px solid ${alpha(theme.palette.common.black, 0.06)}`,
	},

	channelBadge: {
		position: 'absolute',
		bottom: -2,
		right: -2,
		width: '20px',
		height: '20px',
		borderRadius: '50%',
		backgroundColor: theme.palette.background.paper,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		border: `2px solid ${theme.palette.background.paper}`,
		boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
	},

	countNotification: {
		position: 'absolute',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		top: -3,
		left: -3,
		minWidth: '20px',
		height: '20px',
		padding: '0 5px',
		borderRadius: '10px',
		border: `2px solid ${theme.palette.background.paper}`,
		backgroundColor: green[500],
		color: 'white',
		fontSize: '10px',
		fontWeight: 700,

		'&.muted': {
			backgroundColor: grey[500],
		}
	},

	contentSection: {
		minWidth: 0, // Necessário para grid/flex truncation
		display: 'flex',
		flexDirection: 'column',
		gap: '3px',
		overflow: 'hidden',
	},

	headerRow: {
		display: 'flex',
		alignItems: 'center',
		gap: '6px',
		minWidth: 0, // Necessário para Firefox com nested flex
		width: '100%',
	},

	nameSection: {
		flex: 1,
		minWidth: 0,
		overflow: 'hidden',
	},

	contactName: {
		fontSize: '14px',
		fontWeight: 600,
		color: theme.palette.text.primary,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		lineHeight: 1.3,
	},

	pinnedIndicator: {
		fontSize: '14px',
		color: theme.palette.mode === 'dark' ? blue[400] : blue[600],
		flex: '0 0 auto',
	},

	connectionBadge: {
		height: '18px',
		fontSize: '9px',
		fontWeight: 600,
		borderRadius: '9px',
		backgroundColor: theme.palette.mode === 'dark'
			? alpha(blue[500], 0.2)
			: alpha(blue[500], 0.1),
		color: theme.palette.mode === 'dark' ? blue[200] : blue[700],
		border: 'none',
		flex: '0 1 auto',
		maxWidth: '90px',
		'& .MuiChip-label': {
			padding: '0 6px',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
			display: 'block',
		}
	},

	rightSection: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-end',
		gap: '4px',
	},

	timestamp: {
		fontSize: '11px',
		fontWeight: 500,
		color: theme.palette.text.secondary,
		whiteSpace: 'nowrap',
	},

	messageRow: {
		display: 'flex',
		alignItems: 'center',
		gap: '4px',
		minWidth: 0, // Necessário para Firefox com nested flex
		width: '100%',
	},

	messageText: {
		fontSize: '13px',
		color: theme.palette.text.secondary,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		flex: 1,
		lineHeight: 1.4,

		'&.unread': {
			fontWeight: 600,
			color: theme.palette.text.primary,
		}
	},

	messageIcon: {
		fontSize: '15px',
		color: theme.palette.text.secondary,
		marginRight: '3px',
		opacity: 0.75,
		flex: '0 0 auto',
	},

	messageStatus: {
		display: 'flex',
		alignItems: 'center',
		flex: '0 0 auto',
		'& .MuiSvgIcon-root': {
			fontSize: '14px',
		}
	},

	bottomRow: {
		display: 'flex',
		alignItems: 'center',
		gap: '4px',
		marginTop: '2px',
	},

	tagsSection: {
		display: 'flex',
		alignItems: 'center',
		gap: '4px',
		flex: 1,
		overflow: 'hidden',

		'& .MuiChip-root': {
			height: '18px',
			fontSize: '10px',
			fontWeight: 600,
			borderRadius: '9px',
			flex: '0 0 auto',
			'& .MuiChip-label': {
				padding: '0 6px',
			}
		}
	},

	moreTagsIndicator: {
		height: '18px',
		minWidth: '22px',
		borderRadius: '9px',
		backgroundColor: theme.palette.mode === 'dark'
			? alpha(theme.palette.common.white, 0.1)
			: alpha(theme.palette.common.black, 0.06),
		color: theme.palette.text.secondary,
		fontSize: '10px',
		fontWeight: 700,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		cursor: 'pointer',
		flex: '0 0 auto',
		border: theme.palette.mode === 'dark'
			? `1px solid ${alpha(theme.palette.common.white, 0.1)}`
			: `1px solid ${alpha(theme.palette.common.black, 0.08)}`,

		'&:hover': {
			backgroundColor: theme.palette.mode === 'dark'
				? alpha(theme.palette.common.white, 0.15)
				: alpha(theme.palette.common.black, 0.1),
		}
	},

	// ============ PENDING ACTIONS - ESTILO FIXO ============
	pendingActions: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'stretch',
		width: '70px',
		minWidth: '70px',
		maxWidth: '70px',
		gap: '0px',
		flexShrink: 0,
	},

	acceptButton: {
		borderRadius: '5px 5px 0 0',
		padding: '4px 6px',
		fontSize: '11px',
		fontWeight: 700,
		width: '100%',
		minWidth: 'unset',
		maxWidth: '70px',
		height: '24px',
		textTransform: 'none',
		backgroundColor: green[500],
		color: 'white',
		border: 'none',
		boxShadow: 'none',
		lineHeight: 1,
		overflow: 'hidden',
		boxSizing: 'border-box',

		'&:hover': {
			backgroundColor: green[600],
		},

		'&:disabled': {
			backgroundColor: theme.palette.action.disabledBackground,
			color: theme.palette.text.disabled,
		},
	},

	timerChip: {
		width: '100%',
		maxWidth: '70px',
		height: '18px',
		fontSize: '9px',
		fontWeight: 700,
		borderRadius: '0 0 5px 5px',
		border: 'none',
		boxSizing: 'border-box',

		'& .MuiChip-label': {
			padding: '0 6px',
		},

		'&.success': {
			backgroundColor: green[500],
			color: 'white',
		},
		'&.warning': {
			backgroundColor: orange[500],
			color: 'white',
		},
		'&.danger': {
			backgroundColor: red[500],
			color: 'white',
		},
	},

	rescheduledIndicator: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		padding: '4px 6px',
		backgroundColor: theme.palette.mode === 'light'
			? alpha(grey[400], 0.12)
			: alpha(grey[500], 0.2),
		borderRadius: '5px',
		border: `1px solid ${theme.palette.mode === 'light'
			? alpha(grey[400], 0.25)
			: alpha(grey[500], 0.35)}`,
	},

	rescheduledText: {
		fontSize: '9px',
		fontWeight: 600,
		color: theme.palette.mode === 'light' ? grey[600] : grey[400],
		whiteSpace: 'nowrap',
	},

	// ============ FIM PENDING ACTIONS ============

	typingIndicator: {
		display: 'flex',
		alignItems: 'center',
		gap: '4px',
		color: theme.palette.primary.main,
		fontSize: '12px',
		fontStyle: 'italic',

		'& .dots': {
			display: 'flex',
			gap: '2px',
			'& span': {
				width: '3px',
				height: '3px',
				borderRadius: '50%',
				backgroundColor: 'currentColor',
				animation: 'typing 1.4s infinite ease-in-out',
				'&:nth-of-type(2)': { animationDelay: '0.2s' },
				'&:nth-of-type(3)': { animationDelay: '0.4s' }
			}
		}
	},

	priorityIndicator: {
		position: 'absolute',
		left: 0,
		top: 0,
		bottom: 0,
		width: '4px',
		'&.high': { backgroundColor: red[500] },
		'&.medium': { backgroundColor: orange[500] },
		'&.low': { backgroundColor: blue[500] }
	},

	ticketQueueColor: {
		width: '10px',
		borderRadius: '10px 0 0 10px',
		height: '100%',
		position: 'absolute',
		top: 0,
		left: 0,
	},

	divider: {
		marginLeft: '72px',
		backgroundColor: theme.palette.mode === 'dark'
			? alpha(theme.palette.common.white, 0.08)
			: alpha(theme.palette.common.black, 0.06),
	},

	dropdownButton: {
		opacity: 0,
		transition: 'opacity 0.15s ease',
		width: '20px',
		minWidth: '20px',
		height: '20px',
		padding: '2px',
		borderRadius: '4px',
		backgroundColor: 'transparent',
		color: theme.palette.text.secondary,
		flex: '0 0 20px',

		'&:hover': {
			backgroundColor: theme.palette.mode === 'dark'
				? alpha(theme.palette.common.white, 0.1)
				: alpha(theme.palette.common.black, 0.06),
		},

		'&.visible': {
			opacity: 1,
		}
	},

	actionsRow: {
		display: 'flex',
		alignItems: 'center',
		gap: '3px',
		marginTop: '2px',
		flexWrap: 'nowrap',
		maxWidth: '100%',
		overflow: 'hidden',
	},

	ticketMenu: {
		'& .MuiPaper-root': {
			borderRadius: '8px',
			minWidth: '180px',
			boxShadow: theme.palette.mode === 'dark'
				? '0 8px 24px rgba(0,0,0,0.5)'
				: '0 8px 24px rgba(0,0,0,0.12)',
		}
	},

	menuItem: {
		padding: '8px 14px',
		fontSize: '13px',
		fontWeight: 500,
		gap: '10px',

		'& .MuiSvgIcon-root': {
			fontSize: '18px',
			color: theme.palette.text.secondary,
		},

		'&.pin-action:hover .MuiSvgIcon-root': { color: blue[600] },
		'&.resolve-action:hover .MuiSvgIcon-root': { color: green[600] },
		'&.return-action:hover .MuiSvgIcon-root': { color: orange[600] },
		'&.transfer-action:hover .MuiSvgIcon-root': { color: blue[600] },
		'&.group-action:hover .MuiSvgIcon-root': { color: '#7b1fa2' },
		'&.group-action-delete:hover .MuiSvgIcon-root': { color: red[600] },
	},

	menuDivider: {
		margin: '4px 0',
	}
}));

// Keyframes
const keyframesStyles = `
@keyframes typing {
	0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
	30% { transform: translateY(-3px); opacity: 1; }
}
@keyframes spin {
	0% { transform: rotate(0deg); }
	100% { transform: rotate(360deg); }
}
`;

if (typeof document !== 'undefined' && !document.head.querySelector('[data-ticket-animations]')) {
	const style = document.createElement('style');
	style.textContent = keyframesStyles;
	style.setAttribute('data-ticket-animations', 'true');
	document.head.appendChild(style);
}

// Utilitarios
const padTo2Digits = (num) => String(num).padStart(2, '0');

const parseServerDate = (value) => {
	if (!value) return null;
	if (value instanceof Date) return value;
	return new Date(value);
};

const formatTimeDifference = (createdAt) => {
	const created = parseServerDate(createdAt);

	if (!created || isNaN(created.getTime())) {
		return { time: '00:00:00', milliseconds: 0, hours: 0, minutes: 0, seconds: 0 };
	}

	const diff = Math.max(0, Date.now() - created.getTime());
	const hours = Math.floor(diff / (1000 * 60 * 60));
	const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
	const seconds = Math.floor((diff % (1000 * 60)) / 1000);

	return {
		time: `${padTo2Digits(hours)}:${padTo2Digits(minutes)}:${padTo2Digits(seconds)}`,
		milliseconds: diff,
		hours,
		minutes,
		seconds
	};
};

const getRelativeTime = (date) => {
	const now = new Date();
	const messageDate = parseServerDate(date);
	if (!messageDate || isNaN(messageDate.getTime())) return '';
	const diffMinutes = Math.floor((now - messageDate) / (1000 * 60));
	const diffHours = Math.floor(diffMinutes / 60);
	const diffDays = Math.floor(diffHours / 24);

	if (diffMinutes < 1) return 'agora';
	if (diffMinutes < 60) return `${diffMinutes}m`;
	if (diffHours < 24) return `${diffHours}h`;
	if (diffDays < 7) return `${diffDays}d`;

	return isSameDay(messageDate, now)
		? format(messageDate, 'HH:mm')
		: format(messageDate, 'dd/MM');
};

const getUrlTicketPath = () => {
	const url = window.location.href;
	const partesDaURL = url.split('/');
	return partesDaURL[partesDaURL.length - 1];
};

const MESSAGE_TYPES = {
	video: { icon: VideocamIcon, label: "Vídeo" },
	image: { icon: CameraAltIcon, label: "Foto" },
	audio: { icon: MicIcon, label: "Áudio" },
	application: { icon: AttachFileIcon, label: "Arquivo" },
	contact: { icon: ContactIcon, label: "Contato" }
};

// ============ SUBCOMPONENTES ============

const AssignedUserBadge = ({ assigned, classes }) => (
	<Tooltip title={assigned.name} placement="top">
		<Box className={classes.channelBadge}>
			<Avatar
				sx={{
					width: '18px',
					height: '18px',
					fontSize: '9px',
					fontWeight: 600,
					backgroundColor: green[500]
				}}
				alt={assigned.name}
				src={assigned.media}
			>
				{assigned.name.charAt(0)}
			</Avatar>
		</Box>
	</Tooltip>
);

const CountNotification = ({ classes, count }) => {
	if (!count || count === 0) return null;
	return (
		<Box className={classes.countNotification}>
			{count > 99 ? '99+' : count}
		</Box>
	);
};

const MessagePreview = ({ message, classes }) => {
	if (message?.includes('MEDIA_VALUE_')) {
		const renderType = message.split('[[')[1]?.split(']]')[0];
		const typeConfig = MESSAGE_TYPES[renderType];
		if (typeConfig) {
			const IconComponent = typeConfig.icon;
			return (
				<>
					<IconComponent className={classes.messageIcon} />
					<span>{typeConfig.label}</span>
				</>
			);
		}
	}

	if (message?.includes('VCARD')) {
		const lines = message.split("\n");
		const contact = lines.find(line => line.startsWith("FN:"))?.split(":")[1] || "Contato";
		return (
			<>
				<ContactIcon className={classes.messageIcon} />
				<span>{contact}</span>
			</>
		);
	}

	return <MarkdownWrapper>{message || 'Sem mensagens'}</MarkdownWrapper>;
};

const TagsDisplay = ({ tags, classes }) => {
	if (!tags?.length) return null;

	const MAX_VISIBLE_TAGS = 2;
	const visibleTags = tags.slice(0, MAX_VISIBLE_TAGS);
	const hiddenTagsCount = Math.max(0, tags.length - MAX_VISIBLE_TAGS);

	return (
		<Box className={classes.tagsSection}>
			{visibleTags.map((tag) => (
				<Chip
					key={tag.id}
					label={tag.name}
					size="small"
					sx={{
						backgroundColor: tag.color,
						color: isColorDark(tag.color) ? '#ffffff' : '#000000',
					}}
				/>
			))}
			{hiddenTagsCount > 0 && (
				<Tooltip
					title={
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px', maxWidth: '200px' }}>
							{tags.map((tag) => (
								<Chip
									key={tag.id}
									label={tag.name}
									size="small"
									sx={{
										height: '20px',
										fontSize: '10px',
										backgroundColor: tag.color,
										color: isColorDark(tag.color) ? '#ffffff' : '#000000',
									}}
								/>
							))}
						</Box>
					}
					placement="top"
					arrow
				>
					<Box
						className={classes.moreTagsIndicator}
						onClick={(e) => e.stopPropagation()}
					>
						+{hiddenTagsCount}
					</Box>
				</Tooltip>
			)}
		</Box>
	);
};

const PendingTimer = ({ timeInfo, classes }) => {
	const { milliseconds, time } = timeInfo;

	let variant = 'success';
	if (milliseconds > 3600000) variant = 'danger';
	else if (milliseconds > 1800000) variant = 'warning';

	const formatDisplayTime = (timeString) => {
		const [hours, minutes, seconds] = timeString.split(':');
		const h = parseInt(hours);
		const m = parseInt(minutes);
		const s = parseInt(seconds);
		if (h > 0) return `${h}h${m}m`;
		if (m > 0) return `${m}m`;
		return `${s}s`;
	};

	return (
		<Tooltip title={`Aguardando há ${time}`} placement="left" arrow>
			<Chip
				label={formatDisplayTime(time)}
				className={`${classes.timerChip} ${variant}`}
				size="small"
			/>
		</Tooltip>
	);
};

const PendingActions = ({ ticket, onAccept, loading, timeInfo, classes }) => (
	<Box className={classes.pendingActions} onClick={(e) => e.stopPropagation()}>
		<Button
			className={classes.acceptButton}
			variant="contained"
			size="small"
			disabled={loading}
			onClick={(e) => {
				e.stopPropagation();
				onAccept(ticket.id);
			}}
		>
			{loading ? (
				<Box
					sx={{
						width: 10,
						height: 10,
						border: '2px solid currentColor',
						borderTop: '2px solid transparent',
						borderRadius: '50%',
						animation: 'spin 1s linear infinite'
					}}
				/>
			) : 'Aceitar'}
		</Button>
		<PendingTimer timeInfo={timeInfo} classes={classes} />
	</Box>
);

const TicketMenu = ({
	sharedGroup,
	useTransfer,
	anchorEl,
	open,
	onClose,
	ticket,
	classes,
	onPin,
	onResolve,
	onReturn,
	onTransfer,
	onReschedule,
	onShareGroup,
	onShareGroupDelete
}) => {
	const { user } = useContext(AuthContext);
	const isFixed = ticket?.fixed || false;
	const isGroup = (ticket?.contact?.isGroup || ticket?.contact?.number?.length > 13) || false;

	return (
		<Menu
			anchorEl={anchorEl}
			open={open}
			onClose={onClose}
			className={classes.ticketMenu}
			anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
			transformOrigin={{ vertical: 'top', horizontal: 'right' }}
		>
			{(!ticket?.isSharedGroup && useTransfer) && (
				<>
					<MenuItem
						className={`${classes.menuItem} pin-action`}
						onClick={() => { onPin(); onClose(); }}
					>
						{isFixed ? <PushPinIcon /> : <PushPinOutlinedIcon />}
						{isFixed ? 'Desfixar' : 'Fixar'}
					</MenuItem>
					<Divider className={classes.menuDivider} />
				</>
			)}

			<MenuItem className={`${classes.menuItem} resolve-action`} onClick={() => { onResolve(); onClose(); }}>
				<CheckCircleIcon />
				Resolver
			</MenuItem>

			<MenuItem className={`${classes.menuItem} return-action`} onClick={() => { onReturn(); onClose(); }}>
				<ReplyIcon />
				Retornar
			</MenuItem>

			<MenuItem className={`${classes.menuItem} transfer-action`} onClick={() => { onTransfer(); onClose(); }}>
				<TransferWithinAStationIcon />
				Transferir
			</MenuItem>

			{+user?.companyId === 2 && (
				<MenuItem className={classes.menuItem} onClick={() => { onReschedule(); onClose(); }}>
					<ScheduleIcon />
					Reagendar
				</MenuItem>
			)}

			{(!isFixed && isGroup) && (
				<>
					<Divider className={classes.menuDivider} />
					<MenuItem className={`${classes.menuItem} group-action`} onClick={() => { onShareGroup(); onClose(); }}>
						<GroupIcon />
						{ticket.isSharedGroup ? 'Editar grupo' : 'Compartilhar'}
					</MenuItem>
					{ticket.isSharedGroup && (
						<MenuItem className={`${classes.menuItem} group-action-delete`} onClick={() => { onShareGroupDelete(); onClose(); }}>
							<DeleteIcon />
							Remover
						</MenuItem>
					)}
				</>
			)}
		</Menu>
	);
};

// ============ COMPONENTE PRINCIPAL ============

const TicketListItem = React.memo(({
	ticket,
	showAll,
	useTransfer,
	viewTags,
	viewConnections,
	sharedGroup = false,
	notMyUser = false,
	selectable = false,
	selected = false,
	onToggleSelect,
	onContentChange
}) => {
	const { classes } = useStyles();
	const history = useHistory();
	const { ticketId } = useParams();
	const { user } = useContext(AuthContext);
	const isMounted = useRef(true);

	const [loading, setLoading] = useState(false);
	const [timeInfo, setTimeInfo] = useState({ time: '00:00:00', milliseconds: 0 });
	const [currentTime, setCurrentTime] = useState(Date.now());
	const [isHovered, setIsHovered] = useState(false);
	const [menuAnchorEl, setMenuAnchorEl] = useState(null);

	const [confirmDialogs, setConfirmDialogs] = useState({
		pin: false,
		resolve: false,
		return: false,
		shareGroupDelete: false
	});

	const [transferModalOpen, setTransferModalOpen] = useState(false);
	const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);
	const [shareGroupModalOpen, setShareGroupModalOpen] = useState(false);

	const menuOpen = Boolean(menuAnchorEl);

	// Memos
	const isSelected = useMemo(() => {
		const urlTicketCode = getUrlTicketPath();
		return ticketId === ticket.uuid || urlTicketCode === ticket.uuid;
	}, [ticketId, ticket.uuid]);

	const hasUnreadMessages = useMemo(() => ticket.unreadMessages > 0, [ticket.unreadMessages]);
	const shouldShowUser = useMemo(() => showAll && ticket.status === 'open' && ticket?.user?.name, [showAll, ticket.status, ticket?.user?.name]);
	const shouldShowDropdown = useMemo(() => ticket.status === 'open' && isHovered && !menuOpen, [ticket.status, isHovered, menuOpen]);
	const isTicketFixed = useMemo(() => Boolean(ticket.fixed), [ticket.fixed]);
	const relativeTime = useMemo(() => ticket.updatedAt ? getRelativeTime(ticket.updatedAt) : '', [ticket.updatedAt, currentTime]);

	// Effects
	useEffect(() => {
		const interval = setInterval(() => setCurrentTime(Date.now()), 60000);
		return () => clearInterval(interval);
	}, []);

	useEffect(() => {
		if (ticket.status !== 'pending') return;
		setTimeInfo(formatTimeDifference(ticket.createdAt));
		const interval = setInterval(() => setTimeInfo(formatTimeDifference(ticket.createdAt)), 1000);
		return () => clearInterval(interval);
	}, [ticket.createdAt, ticket.status]);

	useEffect(() => {
		onContentChange?.();
	}, [ticket.lastMessage, ticket.tags?.length, ticket.status, ticket.user?.name, ticket.unreadMessages, viewTags, onContentChange]);

	useEffect(() => {
		return () => { isMounted.current = false; };
	}, []);

	// Handlers
	const handleAcceptTicket = useCallback(async (id) => {
		setLoading(true);
		try {
			localStorage.removeItem("ticket_accept");
			await api.put(`/tickets/${id}`, { status: "open", userId: user?.id, accept: true });
			localStorage.setItem("ticket_accept", id);
			document.querySelector("#openTickets")?.click();
			history.push(`/tickets/${ticket.uuid}`);
		} catch (err) {
			ToastError(err);
		} finally {
			if (isMounted.current) setLoading(false);
		}
	}, [user?.id, history, ticket.uuid]);

	const handleSelectTicket = useCallback(() => history.push(`/tickets/${ticket.uuid}`), [history, ticket.uuid]);

	const openConfirmDialog = useCallback((type) => setConfirmDialogs(prev => ({ ...prev, [type]: true })), []);
	const closeConfirmDialog = useCallback((type) => setConfirmDialogs(prev => ({ ...prev, [type]: false })), []);

	const handlePin = useCallback(() => openConfirmDialog('pin'), [openConfirmDialog]);
	const handleResolve = useCallback(() => openConfirmDialog('resolve'), [openConfirmDialog]);
	const handleReturn = useCallback(() => openConfirmDialog('return'), [openConfirmDialog]);
	const handleTransfer = useCallback(() => setTransferModalOpen(true), []);
	const handleReschedule = useCallback(() => setRescheduleModalOpen(true), []);
	const handleShareGroup = useCallback(() => setShareGroupModalOpen(true), []);
	const handleShareGroupDelete = useCallback(() => openConfirmDialog('shareGroupDelete'), [openConfirmDialog]);

	const confirmPin = useCallback(async () => {
		try {
			await api.put(`/tickets/${ticket.id}/pin`, { fixed: !ticket.fixed, userId: user.id });
		} catch (err) {
			ToastError(err);
		}
	}, [ticket.id, ticket.fixed, user.id]);

	const confirmReturn = useCallback(async () => {
		try {
			await api.put(`/tickets/${ticket.id}`, { status: 'pending', userId: null });
			history.push("/tickets");
		} catch (err) {
			ToastError(err);
		}
	}, [ticket.id, history]);

	const confirmShareGroupDelete = useCallback(async () => {
		try {
			await api.delete(`/ticket/${ticket.id}/share-group`);
		} catch (err) {
			ToastError(err);
		}
	}, [ticket.id]);

	const handleRescheduleConfirm = useCallback(async (rescheduleData) => {
		try {
			await api.post('/reschedule-tickets', rescheduleData);
			setRescheduleModalOpen(false);
		} catch (err) {
			ToastError(err);
		}
	}, []);

	const handleShareGroupConfirm = useCallback(async (selectedUsers, isEdit) => {
		const { data } = await (isEdit ? api.put : api.post)(`/ticket/${ticket.id}/share-group`, { users: selectedUsers });
		return data;
	}, [ticket.id]);

	const handleMenuClick = useCallback((event) => {
		event.stopPropagation();
		setMenuAnchorEl(event.currentTarget);
	}, []);

	const handleMenuClose = useCallback(() => setMenuAnchorEl(null), []);
	const handleMouseEnter = useCallback(() => setIsHovered(true), []);
	const handleMouseLeave = useCallback(() => setIsHovered(false), []);

	const getConfirmationMessage = useCallback((action) => {
		const contactName = ticket?.contact?.name || 'contato';
		const messages = {
			pin: isTicketFixed ? `Desfixar conversa com ${contactName}?` : `Fixar conversa com ${contactName}?`,
			resolve: `Resolver ticket de ${contactName}?`,
			return: `Retornar ticket de ${contactName} para a fila?`,
			shareGroupDelete: `Remover compartilhamento do grupo ${contactName}?`
		};
		return messages[action] || 'Confirmar ação?';
	}, [ticket, isTicketFixed]);

	const getConfirmationTitle = useCallback((action) => {
		const titles = {
			pin: isTicketFixed ? 'Desfixar' : 'Fixar',
			resolve: 'Resolver',
			return: 'Retornar',
			shareGroupDelete: 'Remover'
		};
		return titles[action] || 'Confirmar';
	}, [isTicketFixed]);

	// Estilo inline para garantir largura total
	const listItemStyle = {
		width: '100%',
	};

	return (
		<>
			<ListItem
				className={`${classes.chatItem} ${isSelected ? 'selected' : ''} ${hasUnreadMessages ? 'unread' : ''} ${isTicketFixed ? 'pinned' : ''}`}
				style={listItemStyle}
				onClick={handleSelectTicket}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				disablePadding
			>
				{ticket.priority && <Box className={`${classes.priorityIndicator} high`} />}

				<Tooltip arrow placement="right" title={ticket.queue?.name || 'Sem setor'}>
					<Box
						component="span"
						style={{ backgroundColor: ticket.queue?.color || '#7C7C7C' }}
						className={classes.ticketQueueColor}
					/>
				</Tooltip>

				<Box
					className={classes.chatLayout}
					style={selectable ? { gridTemplateColumns: 'auto auto 1fr auto' } : undefined}
				>
					{selectable && (
						<Box sx={{ display: 'flex', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
							<Checkbox
								checked={selected}
								onChange={(e) => { e.stopPropagation(); onToggleSelect?.(); }}
								onClick={(e) => e.stopPropagation()}
								size="small"
								sx={{ p: 0.5 }}
							/>
						</Box>
					)}

					<Box className={classes.avatarSection}>
						<Avatar src={ticket?.contact?.profilePicUrl} className={classes.avatar}>
							{ticket?.contact?.name?.charAt(0)?.toUpperCase()}
						</Avatar>
						{shouldShowUser && ticket.user && <AssignedUserBadge assigned={ticket.user} classes={classes} />}
						<CountNotification count={ticket.unreadMessages} classes={classes} />
					</Box>

					<Box className={classes.contentSection}>
						<Box className={classes.headerRow}>
							<Box className={classes.nameSection}>
								<Typography className={classes.contactName} title={ticket?.contact?.name || ''}>
									{ticket?.contact?.name}
								</Typography>
							</Box>
						</Box>

						<Box className={classes.messageRow}>
							<Typography
								title={ticket?.lastMessage || ''}
								className={`${classes.messageText} ${hasUnreadMessages ? 'unread' : ''}`}
							>
								<MessagePreview message={ticket.lastMessage} classes={classes} />
							</Typography>
							{ticket.lastMessage && (
								<Box className={classes.messageStatus}>
									{ticket.messageStatus === 'sent' && <CheckIcon sx={{ color: grey[500] }} />}
									{ticket.messageStatus === 'delivered' && <DoneAllIcon sx={{ color: grey[500] }} />}
									{ticket.messageStatus === 'read' && <DoneAllIcon sx={{ color: blue[500] }} />}
								</Box>
							)}
						</Box>

						{viewTags && ticket?.contact?.tags?.length > 0 && (
							<Box className={classes.bottomRow}>
								<TagsDisplay tags={ticket?.contact?.tags} classes={classes} />
							</Box>
						)}
					</Box>

					<Box className={classes.rightSection}>
						{ticket.status === 'pending' && !ticket.isRescheduled && (
							<PendingActions
								ticket={ticket}
								onAccept={handleAcceptTicket}
								loading={loading}
								timeInfo={timeInfo}
								classes={classes}
							/>
						)}

						{ticket.status === 'pending' && ticket.isRescheduled && (
							<Box className={classes.rescheduledIndicator}>
								<Typography variant="caption" className={classes.rescheduledText}>
									Reagendado
								</Typography>
							</Box>
						)}

						{ticket.status !== 'pending' && (
							<Typography className={classes.timestamp}>{relativeTime}</Typography>
						)}

						{ticket.status === 'closed' && ticket.rating && (
							<RatingStars value={ticket.rating} size="small" />
						)}

						<Box className={classes.actionsRow}>
							{viewConnections && ticket?.whatsapp?.name && (
								<Tooltip title={`Conexão: ${ticket.whatsapp.name}`}>
									<Chip label={ticket.whatsapp.name} className={classes.connectionBadge} size="small" />
								</Tooltip>
							)}

							{isTicketFixed && (
								<Tooltip title="Fixado" placement="top">
									<PushPinIcon className={classes.pinnedIndicator} />
								</Tooltip>
							)}

							{ticket.status === 'open' && ((sharedGroup && user.profile !== 'user') || !sharedGroup) && (
								<IconButton
									className={`${classes.dropdownButton} ${shouldShowDropdown ? 'visible' : ''}`}
									onClick={handleMenuClick}
									size="small"
								>
									<ArrowDropDownIcon fontSize="small" />
								</IconButton>
							)}
						</Box>
					</Box>
				</Box>
			</ListItem>

			<TicketMenu
				sharedGroup={sharedGroup}
				useTransfer={useTransfer}
				anchorEl={menuAnchorEl}
				open={menuOpen}
				onClose={handleMenuClose}
				ticket={ticket}
				classes={classes}
				onPin={handlePin}
				onResolve={handleResolve}
				onReturn={handleReturn}
				onTransfer={handleTransfer}
				onReschedule={handleReschedule}
				onShareGroup={handleShareGroup}
				onShareGroupDelete={handleShareGroupDelete}
			/>

			<ConfirmationModal title={getConfirmationTitle('pin')} open={confirmDialogs.pin} onClose={() => closeConfirmDialog('pin')} onConfirm={confirmPin}>
				{getConfirmationMessage('pin')}
			</ConfirmationModal>

			<ReasonForContact open={confirmDialogs.resolve} onClose={() => closeConfirmDialog('resolve')} ticketId={ticket.id} />

			<ConfirmationModal title={getConfirmationTitle('return')} open={confirmDialogs.return} onClose={() => closeConfirmDialog('return')} onConfirm={confirmReturn}>
				{getConfirmationMessage('return')}
			</ConfirmationModal>

			<ConfirmationModal title={getConfirmationTitle('shareGroupDelete')} open={confirmDialogs.shareGroupDelete} onClose={() => closeConfirmDialog('shareGroupDelete')} onConfirm={confirmShareGroupDelete}>
				{getConfirmationMessage('shareGroupDelete')}
			</ConfirmationModal>

			<TransferTicketPendingModalCustom modalOpen={transferModalOpen} onClose={() => setTransferModalOpen(false)} ticket={ticket} />
			<RescheduleTicket open={rescheduleModalOpen} onClose={() => setRescheduleModalOpen(false)} onConfirm={handleRescheduleConfirm} ticket={ticket} />
			<ShareGroupModal open={shareGroupModalOpen} onClose={() => setShareGroupModalOpen(false)} onConfirm={handleShareGroupConfirm} ticket={ticket} />

			<Divider className={classes.divider} />
		</>
	);
});

TicketListItem.displayName = 'TicketListItem';

const areEqual = (prevProps, nextProps) => {
	if (prevProps.ticket.id !== nextProps.ticket.id) return false;
	if (prevProps.ticket.lastMessage !== nextProps.ticket.lastMessage) return false;
	if (prevProps.ticket.unreadMessages !== nextProps.ticket.unreadMessages) return false;
	if (prevProps.ticket.status !== nextProps.ticket.status) return false;
	if (prevProps.selected !== nextProps.selected) return false;
	if (prevProps.ticket.fixed !== nextProps.ticket.fixed) return false;
	if (prevProps.ticket.user?.name !== nextProps.ticket.user?.name) return false;
	if (prevProps.viewTags !== nextProps.viewTags) return false;
	if (prevProps.viewConnections !== nextProps.viewConnections) return false;
	const prevTags = prevProps.ticket.tags || [];
	const nextTags = nextProps.ticket.tags || [];
	if (prevTags.length !== nextTags.length) return false;
	return true;
};

export default React.memo(TicketListItem, areEqual);
